import React, { useState, useEffect } from 'react';
import { 
  CreditCard, Check, X, AlertCircle, Eye, EyeOff, 
  ExternalLink, Save, RefreshCw, CheckCircle
} from 'lucide-react';
import axios from 'axios';

const PaymentSettings = () => {
  const [activeTab, setActiveTab] = useState('stripe');
  const [gateways, setGateways] = useState({
    stripe: { enabled: false, api_key_test: '', api_key_live: '', merchant_id_test: '', merchant_id_live: '', environment: 'test', is_verified: false },
    square: { enabled: false, api_key_test: '', api_key_live: '', merchant_id_test: '', merchant_id_live: '', environment: 'test', is_verified: false },
    clover: { enabled: false, api_key_test: '', api_key_live: '', merchant_id_test: '', merchant_id_live: '', environment: 'test', is_verified: false },
    toast: { enabled: false, api_key_test: '', api_key_live: '', merchant_id_test: '', merchant_id_live: '', environment: 'test', is_verified: false },
    link: { enabled: false, api_key_test: '', api_key_live: '', merchant_id_test: '', merchant_id_live: '', environment: 'test', is_verified: false }
  });
  
  // Store real keys that were just entered (not masked)
  const [realKeys, setRealKeys] = useState({});
  
  const [showKeys, setShowKeys] = useState({
    stripe_test: false,
    stripe_live: false,
    square_test: false,
    square_live: false,
    clover_test: false,
    clover_live: false,
    toast_test: false,
    toast_live: false,
    link_test: false,
    link_live: false
  });
  
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(null);
  const [notification, setNotification] = useState({ show: false, message: '', type: '' });
  const [verifyModal, setVerifyModal] = useState({ show: false, loading: false, success: false, error: false, message: '' });

  const gatewayTabs = [
    { id: 'stripe', label: 'Stripe', icon: CreditCard, color: 'var(--theme-primary)' },
    { id: 'square', label: 'Square POS', icon: CreditCard, color: 'var(--theme-primary)' },
    { id: 'clover', label: 'Clover', icon: CreditCard, color: 'var(--theme-primary)' },
    { id: 'toast', label: 'Toast Tab POS', icon: CreditCard, color: 'var(--theme-primary)' },
    { id: 'link', label: 'Link Payment', icon: CreditCard, color: 'var(--theme-primary)' }
  ];

  
  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    fetchGatewayConfigs();
  }, []);

  const fetchGatewayConfigs = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${backendUrl}/api/payment-gateways`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        const configs = {};
        response.data.gateways.forEach(gw => {
          // Keep masked values visible in inputs to indicate credentials are saved
          configs[gw.gateway] = {
            ...gw,
            // Add flags to indicate if keys are masked
            _masked_api_test: gw.api_key_test && gw.api_key_test.startsWith('***'),
            _masked_api_live: gw.api_key_live && gw.api_key_live.startsWith('***'),
            _masked_pub_test: gw.publishable_key_test && gw.publishable_key_test.startsWith('***'),
            _masked_pub_live: gw.publishable_key_live && gw.publishable_key_live.startsWith('***'),
            // Keep masked values to show in inputs (they indicate credentials exist)
            api_key_test: gw.api_key_test || '',
            api_key_live: gw.api_key_live || '',
            publishable_key_test: gw.publishable_key_test || '',
            publishable_key_live: gw.publishable_key_live || '',
            merchant_id_test: gw.merchant_id_test || '',
            merchant_id_live: gw.merchant_id_live || ''
          };
        });
        setGateways(prev => {
          // Merge with existing state, preserving any user edits
          const merged = { ...prev };
          Object.keys(configs).forEach(key => {
            merged[key] = { ...merged[key], ...configs[key] };
          });
          return merged;
        });
      }
    } catch (error) {
      console.error('Error fetching gateway configs:', error);
    }
  };

  const showNotification = (message, type) => {
    setNotification({ show: true, message, type });
    setTimeout(() => setNotification({ show: false, message: '', type: '' }), 4000);
  };

  const handleSaveGateway = async (gateway, overrideData = null) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const dataToSave = overrideData || gateways[gateway];
      const response = await axios.post(
        `${backendUrl}/api/payment-gateways`,
        {
          gateway: gateway,
          ...dataToSave
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      if (response.data.success) {
        showNotification(`${gateway.toUpperCase()} configuration saved successfully!`, 'success');
        fetchGatewayConfigs();
      }
    } catch (error) {
      console.error('Error saving gateway:', error);
      showNotification(error.response?.data?.detail || 'Failed to save configuration', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyCredentials = async (gateway) => {
    setVerifying(gateway);
    // Show modal with loading state
    setVerifyModal({ show: true, loading: true, success: false, error: false, message: 'Verifying credentials...' });
    
    try {
      const token = localStorage.getItem('token');
      const config = gateways[gateway];
      
      // Use real key if just entered, otherwise let backend use saved key
      const apiKeyField = config.environment === 'test' ? 'api_key_test' : 'api_key_live';
      const merchantIdField = config.environment === 'test' ? 'merchant_id_test' : 'merchant_id_live';
      
      const apiKey = realKeys[`${gateway}_${apiKeyField}`] || config[apiKeyField];
      const merchantId = config[merchantIdField];
      
      // If key is masked, don't send it - let backend use the real one from DB
      const payload = {
        gateway: gateway,
        environment: config.environment
      };
      
      // Only send keys if they're not masked (real keys)
      if (apiKey && !apiKey.startsWith('***')) {
        payload.api_key = apiKey;
      }
      if (merchantId && !String(merchantId).startsWith('***')) {
        payload.merchant_id = merchantId;
      }
      
      const response = await axios.post(
        `${backendUrl}/api/payment-gateways/verify`,
        payload,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      if (response.data.success && response.data.verified) {
        setGateways(prev => ({
          ...prev,
          [gateway]: { ...prev[gateway], is_verified: true }
        }));
        // Show success in modal
        setVerifyModal({ show: true, loading: false, success: true, error: false, message: `${gateway.toUpperCase()} credentials verified successfully!` });
      }
    } catch (error) {
      console.error('Error verifying credentials:', error);
      // Show error in modal
      setVerifyModal({ show: true, loading: false, success: false, error: true, message: error.response?.data?.detail || 'Failed to verify credentials' });
    } finally {
      setVerifying(null);
    }
  };

  const setupInstructions = {
    stripe: {
      title: 'Stripe Payment Gateway',
      steps: [
        'Go to stripe.com and create an account or sign in',
        'Navigate to Developers > API keys in your Stripe Dashboard',
        'Copy your Publishable key and Secret key for test mode',
        'For live mode, toggle to "Live mode" and copy those keys',
        'Paste the keys below and save'
      ],
      links: [
        { text: 'Stripe Dashboard', url: 'https://dashboard.stripe.com' },
        { text: 'API Documentation', url: 'https://stripe.com/docs/api' }
      ]
    },
    square: {
      title: 'Square POS Payment Gateway',
      steps: [
        'Go to squareup.com and create a developer account',
        'Create a new application in the Square Developer Dashboard',
        'Navigate to the Credentials section',
        'Copy your Sandbox Access Token and Application ID',
        'For production, switch to Production and copy those credentials',
        'Paste the credentials below and save'
      ],
      links: [
        { text: 'Square Developer Portal', url: 'https://developer.squareup.com' },
        { text: 'Getting Started Guide', url: 'https://developer.squareup.com/docs/get-started' }
      ]
    },
    clover: {
      title: 'Clover Payment Gateway',
      steps: [
        'Go to clover.com/developers and create a developer account',
        'Create a test merchant account in the sandbox',
        'Generate an API token from your test merchant dashboard',
        'Note your Merchant ID from the account settings',
        'For production, repeat with your live merchant account',
        'Paste the credentials below and save'
      ],
      links: [
        { text: 'Clover Developer Portal', url: 'https://www.clover.com/developers' },
        { text: 'API Documentation', url: 'https://docs.clover.com/docs' }
      ]
    },
    toast: {
      title: 'Toast Tab POS Payment Gateway',
      steps: [
        'Go to toasttab.com/partners and apply to become an integration partner',
        'Once approved, you will receive access to the Toast Developer Portal',
        'Log in to the Developer Portal and navigate to Credentials',
        'Copy your Client ID and Client Secret for sandbox environment',
        'For production, contact Toast Developer Relations for production credentials',
        'Paste the credentials below and save'
      ],
      links: [
        { text: 'Toast Partner Connect', url: 'https://pos.toasttab.com/partners' },
        { text: 'Developer Documentation', url: 'https://doc.toasttab.com/doc/devguide/' }
      ]
    },
    link: {
      title: 'Link Payment Gateway (Stripe Link)',
      steps: [
        'Go to stripe.com and create an account or sign in',
        'Navigate to Developers > API keys in your Stripe Dashboard',
        'Copy your Publishable key and Secret key for test mode',
        'Enable Stripe Link in your Dashboard under Settings > Payment methods',
        'For live mode, toggle to "Live mode" and copy those keys',
        'Paste the keys below and save'
      ],
      links: [
        { text: 'Stripe Dashboard', url: 'https://dashboard.stripe.com' },
        { text: 'Link Documentation', url: 'https://stripe.com/docs/payments/link' }
      ]
    }
  };

  const renderGatewayCard = (gatewayKey, gatewayName) => {
    const config = gateways[gatewayKey];
    const instructions = setupInstructions[gatewayKey];
    
    return (
      <div key={gatewayKey} className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        {/* Header */}
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <CreditCard className="h-6 w-6 text-theme-blue mr-3" />
              <div>
                <h3 className="text-lg font-semibold text-gray-900">{gatewayName}</h3>
                <p className="text-sm text-gray-500 mt-1">
                  {config.is_verified ? (
                    <span className="flex items-center text-green-600">
                      <CheckCircle className="h-4 w-4 mr-1" />
                      Verified
                    </span>
                  ) : (
                    'Not verified'
                  )}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={config.enabled}
                  onChange={async (e) => {
                    const newEnabled = e.target.checked;
                    // Update local state
                    setGateways(prev => ({
                      ...prev,
                      [gatewayKey]: { ...prev[gatewayKey], enabled: newEnabled }
                    }));
                    // Auto-save with updated enabled value
                    const updatedConfig = { ...config, enabled: newEnabled };
                    await handleSaveGateway(gatewayKey, updatedConfig);
                  }}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-cyan-300 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all" style={{ backgroundColor: config.enabled ? 'var(--theme-primary)' : '' }}></div>
                <span className="ms-3 text-sm font-medium text-gray-700">
                  {config.enabled ? 'Enabled' : 'Disabled'}
                </span>
              </label>
            </div>
          </div>
        </div>

        {/* Configuration Panel - Always Visible */}
        <div className="p-6 bg-gray-50">
            {/* Setup Instructions */}
            <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <h4 className="text-sm font-semibold text-blue-900 mb-3">Setup Instructions</h4>
              <ol className="list-decimal list-inside space-y-2 text-sm text-blue-800">
                {instructions.steps.map((step, idx) => (
                  <li key={idx}>{step}</li>
                ))}
              </ol>
              <div className="mt-3 flex gap-3">
                {instructions.links.map((link, idx) => (
                  <a
                    key={idx}
                    href={link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-theme-blue hover:text-blue-700 flex items-center"
                  >
                    {link.text}
                    <ExternalLink className="h-3 w-3 ml-1" />
                  </a>
                ))}
              </div>
            </div>

            {/* Environment Selection */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Environment
              </label>
              <div className="flex gap-4">
                <label className="flex items-center">
                  <input
                    type="radio"
                    checked={config.environment === 'test'}
                    onChange={() => setGateways(prev => ({
                      ...prev,
                      [gatewayKey]: { ...prev[gatewayKey], environment: 'test' }
                    }))}
                    className="h-4 w-4 text-theme-blue focus:ring-blue-500 border-gray-300"
                  />
                  <span className="ml-2 text-sm text-gray-700">Test/Sandbox</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="radio"
                    checked={config.environment === 'live'}
                    onChange={() => setGateways(prev => ({
                      ...prev,
                      [gatewayKey]: { ...prev[gatewayKey], environment: 'live' }
                    }))}
                    className="h-4 w-4 text-theme-blue focus:ring-blue-500 border-gray-300"
                  />
                  <span className="ml-2 text-sm text-gray-700">Live/Production</span>
                </label>
              </div>
            </div>

            {/* API Credentials */}
            <div className="grid grid-cols-1 gap-6">
              {/* Credentials based on selected environment */}
              <div>
                <h4 className="text-sm font-semibold text-gray-900 mb-3">
                  {config.environment === 'test' ? 'Test/Sandbox Credentials' : 'Live/Production Credentials'}
                </h4>
                <div className="space-y-4">
                  {/* API Key field - label changes based on gateway */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {gatewayKey === 'stripe' || gatewayKey === 'link' 
                        ? (config.environment === 'test' ? 'Test Secret Key' : 'Live Secret Key')
                        : (config.environment === 'test' ? 'Test API Key / Access Token' : 'Live API Key / Access Token')
                      }
                    </label>
                    <div className="relative">
                      <input
                        type={showKeys[`${gatewayKey}_${config.environment}`] ? 'text' : 'password'}
                        value={
                          // Show real key if we have it, otherwise show the value from gateway config
                          realKeys[`${gatewayKey}_${config.environment === 'test' ? 'api_key_test' : 'api_key_live'}`] || 
                          (config.environment === 'test' ? config.api_key_test : config.api_key_live)
                        }
                        onChange={(e) => {
                          const newValue = e.target.value;
                          // Store the real key
                          setRealKeys(prev => ({
                            ...prev,
                            [`${gatewayKey}_${config.environment === 'test' ? 'api_key_test' : 'api_key_live'}`]: newValue
                          }));
                          // Update gateway config
                          setGateways(prev => ({
                            ...prev,
                            [gatewayKey]: { 
                              ...prev[gatewayKey], 
                              [config.environment === 'test' ? 'api_key_test' : 'api_key_live']: newValue,
                              // Clear masked flag when user types
                              [config.environment === 'test' ? '_masked_test' : '_masked_live']: false
                            }
                          }));
                        }}
                        placeholder={
                          (config.environment === 'test' && config._masked_test) || 
                          (config.environment === 'live' && config._masked_live)
                            ? 'Key is saved - enter new key to update'
                            : `Enter ${config.environment} secret key...`
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent pr-10"
                      />
                      <button
                        onClick={() => setShowKeys(prev => ({ 
                          ...prev, 
                          [`${gatewayKey}_${config.environment}`]: !prev[`${gatewayKey}_${config.environment}`] 
                        }))}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      >
                        {showKeys[`${gatewayKey}_${config.environment}`] ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                    {((config.environment === 'test' && config._masked_test) || 
                      (config.environment === 'live' && config._masked_live)) && (
                      <p className="mt-1 text-xs text-green-600">
                        ✓ Credentials are saved and hidden for security
                      </p>
                    )}
                  </div>
                  
                  {/* Publishable Key for Stripe and Link */}
                  {(gatewayKey === 'stripe' || gatewayKey === 'link') && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {config.environment === 'test' ? 'Test Publishable Key' : 'Live Publishable Key'}
                      </label>
                      <input
                        type="text"
                        value={config.environment === 'test' ? config.publishable_key_test : config.publishable_key_live}
                        onChange={(e) => setGateways(prev => ({
                          ...prev,
                          [gatewayKey]: { 
                            ...prev[gatewayKey], 
                            [config.environment === 'test' ? 'publishable_key_test' : 'publishable_key_live']: e.target.value 
                          }
                        }))}
                        placeholder={`Enter ${config.environment} publishable key...`}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                  )}
                  
                  {/* Merchant ID for Square, Clover, and Toast */}
                  {(gatewayKey === 'square' || gatewayKey === 'clover' || gatewayKey === 'toast') && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {config.environment === 'test' ? 'Test Merchant ID / Application ID' : 'Live Merchant ID / Application ID'}
                      </label>
                      <input
                        type="text"
                        value={config.environment === 'test' ? config.merchant_id_test : config.merchant_id_live}
                        onChange={(e) => setGateways(prev => ({
                          ...prev,
                          [gatewayKey]: { 
                            ...prev[gatewayKey], 
                            [config.environment === 'test' ? 'merchant_id_test' : 'merchant_id_live']: e.target.value 
                          }
                        }))}
                        placeholder={`Enter ${config.environment} merchant ID...`}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="mt-6 flex justify-end gap-3">
              <button
                onClick={() => handleVerifyCredentials(gatewayKey)}
                disabled={verifying === gatewayKey}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50 flex items-center"
              >
                {verifying === gatewayKey ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-700 mr-2"></div>
                    Verifying...
                  </>
                ) : (
                  <>
                    <CheckCircle className="h-4 w-4 mr-2" />
                    Verify Credentials
                  </>
                )}
              </button>
              <button
                onClick={() => handleSaveGateway(gatewayKey)}
                disabled={loading}
                style={{ backgroundColor: loading ? 'rgba(41, 173, 211, 0.6)' : 'var(--theme-primary)' }}
                className="px-6 py-2 text-white rounded-lg hover:opacity-90 transition-all disabled:opacity-50 flex items-center"
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4 mr-2" />
                    Save Configuration
                  </>
                )}
              </button>
            </div>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        
        {/* Notification */}
        {notification.show && (
          <div className={`fixed top-4 right-4 z-50 px-4 py-3 rounded-lg shadow-lg ${
            notification.type === 'success' ? 'bg-green-500' : 'bg-red-500'
          } text-white flex items-center gap-2`}>
            {notification.type === 'success' ? <CheckCircle className="h-5 w-5" /> : <AlertCircle className="h-5 w-5" />}
            {notification.message}
          </div>
        )}

        {/* Header */}
        <div className="bg-white rounded-xl shadow-sm mb-6">
          <div className="p-4 border-b border-gray-200">
            <h1 className="text-lg font-semibold text-gray-900">Payment Gateways</h1>
          </div>

          {/* Tabs */}
          <div className="flex border-b border-gray-200 overflow-x-auto">
            {gatewayTabs.map(tab => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 px-6 py-3 font-medium text-sm border-b-2 transition-colors whitespace-nowrap ${
                    activeTab === tab.id
                      ? 'border-[var(--theme-primary)] text-[var(--theme-primary)]'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                  style={activeTab === tab.id ? { borderBottomColor: tab.color } : {}}
                >
                  <Icon className="h-4 w-4" />
                  {tab.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Tab Content */}
        <div className="bg-white rounded-xl shadow-sm">
          {renderGatewayCard(activeTab, gatewayTabs.find(t => t.id === activeTab)?.label)}
        </div>

        {/* Info Banner */}
        <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex">
            <AlertCircle className="h-5 w-5 text-theme-blue mr-3 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-blue-800">
              <p className="font-medium mb-1">Important Security Notes:</p>
              <ul className="list-disc list-inside space-y-1">
                <li>Always use test/sandbox credentials for testing</li>
                <li>Only enable live mode after thorough testing</li>
                <li>Keep your API keys secure and never share them</li>
                <li>Verify credentials before enabling a gateway</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Verification Modal */}
      {verifyModal.show && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 max-w-sm mx-4">
            <div className="flex flex-col items-center">
              {verifyModal.loading && (
                <>
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-theme-blue mb-4"></div>
                  <p className="text-sm text-gray-700 text-center">{verifyModal.message}</p>
                </>
              )}
              
              {verifyModal.success && (
                <>
                  <div className="rounded-full bg-green-100 p-3 mb-4">
                    <CheckCircle className="h-8 w-8 text-green-600" />
                  </div>
                  <p className="text-sm text-gray-900 font-medium text-center mb-4">{verifyModal.message}</p>
                  <button
                    onClick={() => setVerifyModal({ show: false, loading: false, success: false, error: false, message: '' })}
                    className="text-white text-sm px-4 py-2 rounded-md transition-colors font-medium"
                    style={{ backgroundColor: 'var(--theme-primary)' }}
                    onMouseEnter={(e) => e.target.style.backgroundColor = 'var(--theme-primary-hover)'}
                    onMouseLeave={(e) => e.target.style.backgroundColor = 'var(--theme-primary)'}
                  >
                    Close
                  </button>
                </>
              )}
              
              {verifyModal.error && (
                <>
                  <div className="rounded-full bg-red-100 p-3 mb-4">
                    <X className="h-8 w-8 text-red-600" />
                  </div>
                  <p className="text-sm text-gray-900 font-medium text-center mb-4">{verifyModal.message}</p>
                  <button
                    onClick={() => setVerifyModal({ show: false, loading: false, success: false, error: false, message: '' })}
                    className="bg-gray-500 hover:bg-gray-600 text-white text-sm px-4 py-2 rounded-md transition-colors font-medium"
                  >
                    Close
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PaymentSettings;
